/**
 * Skill Level Color System
 * 
 * Each color key maps to a bg (badge background), text (badge text color), 
 * and dot (the small circle indicator) value.
 * 
 * Stored in SkillLevel.color as the key string (e.g. "green", "blue").
 */

export interface SkillLevelColorSet {
    bg: string;
    text: string;
    dot: string;
}

export const SKILL_LEVEL_COLORS: Record<string, SkillLevelColorSet> = {
    green: { bg: "#dcfce7", text: "#166534", dot: "#22c55e" },
    blue: { bg: "#dbeafe", text: "#1e40af", dot: "#3b82f6" },
    amber: { bg: "#fef3c7", text: "#92400e", dot: "#f59e0b" },
    red: { bg: "#fee2e2", text: "#991b1b", dot: "#ef4444" },
    purple: { bg: "#ede9fe", text: "#5b21b6", dot: "#8b5cf6" },
    teal: { bg: "#ccfbf1", text: "#115e59", dot: "#14b8a6" },
    gray: { bg: "#f1f5f9", text: "#64748b", dot: "#94a3b8" },
    pink: { bg: "#fce7f3", text: "#9d174d", dot: "#ec4899" },
};

/** All available color keys for the picker */
export const COLOR_OPTIONS = Object.keys(SKILL_LEVEL_COLORS);

/** Default colors assigned to the 4 default levels */
export const DEFAULT_LEVEL_COLORS: Record<string, string> = {
    "Good": "green",
    "Average": "blue",
    "Developing": "amber",
    "Not Evaluated": "gray",
};

/**
 * Sort skill levels with "Not Evaluated" always last.
 */
export function sortSkillLevels<T extends { name: string; sortOrder: number }>(levels: T[]): T[] {
    return [...levels].sort((a, b) => {
        if (a.name === "Not Evaluated") return 1;
        if (b.name === "Not Evaluated") return -1;
        return a.sortOrder - b.sortOrder;
    });
}

export function getSkillLevelColor(
    levelName: string | null | undefined,
    skillLevels: { name: string; color?: string | null }[]
): SkillLevelColorSet {
    if (!levelName) return SKILL_LEVEL_COLORS.gray;

    // Try to find from the company's skill level records
    const record = skillLevels.find((sl) => sl.name === levelName);
    if (record?.color && SKILL_LEVEL_COLORS[record.color]) {
        return SKILL_LEVEL_COLORS[record.color];
    }

    // Fallback: check default level color mapping by name
    const defaultColor = DEFAULT_LEVEL_COLORS[levelName];
    if (defaultColor && SKILL_LEVEL_COLORS[defaultColor]) {
        return SKILL_LEVEL_COLORS[defaultColor];
    }

    // Final fallback
    return SKILL_LEVEL_COLORS.gray;
}
